// Module `cm`: the contract manager view (docs/v3/FEEDBACK-PLAN.md, phase 2).
// The Commission's contract manager sees one project, read-only, and decides
// on Terms of Reference. Every body here is built for that view: counts,
// figures and roles, never a person's name, CV or daily rate (decision 6).
// The only names shown are the contract manager's own and the implementer's
// contract lead (who wrote to them).
import { z } from "zod";
import { uuid } from "../schemas/common";
import { email, text, textOrEmpty, type Id } from "./common";
import type { MailStatus } from "./mail";

// ---------- access (project side) ----------

export const CM_STATUSES = ["invited", "active", "revoked"] as const;
export type CmStatus = (typeof CM_STATUSES)[number];

/** GET /api/p/:projectId/contract-managers: one row per person invited. */
export type ContractManagerRow = {
  id: Id;
  name: string;
  email: string;
  /** "Contract manager, DG NEAR" */
  label: string;
  status: CmStatus;
  invitedAt: string;
  acceptedAt: string | null;
  lastSeenAt: string | null;
  /** The latest invitation email: queued, sent, trying again or failed (EM3). */
  mail: MailStatus | null;
};

/** POST /contract-managers and .../resend: the row, plus a warning when the address is a team member's (GL5). */
export type ContractManagerInvited = ContractManagerRow & { warning: string | null };

/**
 * CO1 (workbook 27 Oct): "Contract manager: manager · marius@convenor.eu". A
 * role word is not a name: the invitation asks for the person's own name.
 */
export const CM_ROLE_WORDS = /^(the\s+)?(contract\s+)?(manager|cm|admin|administrator|funder|commission|eu|dg|test|user|contact)$/i;
export const isRoleNotName = (name: string): boolean => CM_ROLE_WORDS.test(name.trim().replace(/\s+/g, " "));

export const cmInviteBody = z.object({
  name: text(120, "Name").refine((n) => !isRoleNotName(n), "Give the person's name, for example Cecile Plunet. Their role goes under Label."),
  email,
  label: textOrEmpty(120),
  /** GL5: the address is someone of the own team, and the lead confirmed the invitation anyway. */
  confirmTeamMember: z.boolean().optional(),
});

// ---------- joining (/cm/join/:token) ----------

/** GET /api/cm-join/:token */
export type CmJoinInfo = {
  orgName: string;
  projectName: string;
  reference: string;
  name: string;
  email: string;
  accountExists: boolean;
};

export const cmJoinAcceptBody = z.object({ termsAccepted: z.boolean().optional() });

// ---------- the view (/api/cm/:projectId/...) ----------

/** The header of every screen. */
export type CmHeader = {
  projectId: Id;
  project: string;
  reference: string;
  orgName: string;
  /** "A. Moreau · Contract manager, DG NEAR" */
  viewer: string;
  /** "month 14 of 36", or null without contract dates */
  month: string | null;
  torsWaiting: number;
  /** CM-N3 (retest 30 Oct): the project team previews what the contract manager sees; nothing can be decided or sent. */
  preview?: boolean;
};

/** GET /api/cm: the projects this person may see. */
export type CmProjects = { projects: { id: Id; name: string; reference: string; orgName: string }[] };

export type CmFigure = { label: string; value: string; sub: string | null };

/** GET /api/cm/:projectId/overview */
export type CmOverview = {
  header: CmHeader;
  /** "2 Terms of Reference wait for your decision. The oldest has waited 9 days." or null */
  waiting: string | null;
  figures: CmFigure[];
  comingUp: { date: string; label: string }[];
  recentlyDone: { activityId: Id; label: string; sub: string; evidenceComplete: boolean }[];
};

export type CmTorRow = {
  id: Id;
  code: string;
  title: string;
  version: number;
  /** "12 Sep" */
  submitted: string | null;
  waitingDays: number | null;
  budget: string;
  /** decided rows only */
  decision: "approved" | "changes_requested" | null;
  decided: string | null;
};

/** GET /api/cm/:projectId/tors */
export type CmTorQueue = { header: CmHeader; waiting: CmTorRow[]; decided: CmTorRow[] };

export type CmDecision = {
  decision: "approved" | "changes_requested";
  /** "Approved by A. Moreau on 28 Aug 2026" */
  line: string;
  note: string | null;
};

/** GET /api/cm/:projectId/tors/:torId */
export type CmTorReview = {
  header: CmHeader;
  id: Id;
  code: string;
  title: string;
  version: number;
  /** "Version 2 · submitted 12 Sep by M. Kovač, contract lead · waiting 9 days" */
  line: string;
  /** Waiting for this person's decision. */
  canDecide: boolean;
  /** Plain sentences: "Budget lowered from €41,000 to €38,400". Empty on version 1. */
  changes: string[];
  previousVersion: number | null;
  /** The team's note when they sent this version after changes were requested ("what changed"). */
  teamNote?: string | null;
  facts: { label: string; value: string }[];
  /** The objective as sent. */
  objective: string[];
  /** The latest decision on this version, if any. */
  decision: CmDecision | null;
};

export const CM_DECISIONS = ["approve", "request_changes"] as const;
export const cmDecisionBody = z
  .object({
    decision: z.enum(CM_DECISIONS),
    note: textOrEmpty(4000),
    fileId: uuid.nullable(),
  })
  .refine((b) => b.decision === "approve" || b.note.trim().length > 0, { message: "Say what should change.", path: ["note"] });

export const CM_EXPORT_FORMATS = ["pdf", "docx"] as const;
export const cmExportQuery = z.object({ format: z.enum(CM_EXPORT_FORMATS).default("pdf") });

/** GET /api/cm/:projectId/activities */
export type CmActivities = {
  header: CmHeader;
  groups: { result: string; rows: { id: Id; code: string; title: string; dates: string; status: string; done: boolean }[] }[];
};

/** GET /api/cm/:projectId/activities/:activityId */
export type CmActivity = {
  header: CmHeader;
  id: Id;
  /** "Activity A2.2 · Output 2.1" */
  kicker: string;
  title: string;
  /** "3 to 4 September 2026 · done · ToR approved 28 Aug" */
  line: string;
  figures: CmFigure[];
  summary: string[];
  outcomes: string[];
  evidence: { label: string; onFile: boolean }[];
};

export type CmIndicatorRow = {
  code: string;
  /** the full name */
  name: string;
  /** a few plain words, null = none yet */
  shortName: string | null;
  baseline: string;
  target: string;
  current: string;
  /** 0-100, or null when it cannot be computed */
  progress: number | null;
  behind: boolean;
  /** "3 readings" */
  source: string;
};

/** GET /api/cm/:projectId/indicators */
export type CmIndicators = { header: CmHeader; groups: { result: string; rows: CmIndicatorRow[] }[] };

/** GET /api/cm/:projectId/budget */
export type CmBudget = {
  header: CmHeader;
  lines: { heading: string; contract: string; approved: string; spent: string; left: string }[];
  total: { contract: string; approved: string; spent: string; left: string };
  /** "€60,300 is waiting on the two ToRs in your queue." or null */
  waitingNote: string | null;
  experts: { label: string; contractDays: string; used: string; timesheets: string }[];
};

/** GET /api/cm/:projectId/reports */
export type CmReports = {
  header: CmHeader;
  reports: { id: Id; title: string; line: string; exportable: boolean }[];
  decisionLog: { date: string; text: string }[];
};

export const CM_DOCUMENT_TYPES = ["approval_letter", "amendment", "report_acceptance", "comments", "other"] as const;
export type CmDocumentType = (typeof CM_DOCUMENT_TYPES)[number];
export const CM_DOCUMENT_TYPE_LABELS: Record<CmDocumentType, string> = {
  approval_letter: "Approval letter",
  amendment: "Amendment",
  report_acceptance: "Report acceptance",
  comments: "Comments",
  other: "Other",
};

/** The file_link role of a document the contract manager shared: "cm:<type>". */
export const cmDocumentRole = (t: CmDocumentType) => `cm:${t}`;

/** A thing a document can be filed with: "tor:<id>", "report:<id>", "monthly:<id>" or "contract". */
export const cmTarget = z.string().regex(/^(contract|(tor|report|monthly):[0-9a-f-]{36})$/, "Choose what the document belongs to.");

/** GET /api/cm/:projectId/documents */
export type CmDocuments = {
  header: CmHeader;
  targets: { value: string; label: string }[];
  rows: { fileId: Id; name: string; type: string; linkedTo: string; from: string; date: string; mine: boolean }[];
};

/** POST /api/cm/:projectId/documents (multipart: file, type, target, note?) */
export type CmDocumentResult = { fileId: Id };

// ---------- monthly reports (phase 3) ----------

/** "2026-08" */
export const cmMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Choose a month.");

/** One figure Convenor fills in from the records; the team unticks what it does not share. */
export type MonthlyFigure = { key: string; label: string; value: string; include: boolean };

export const MONTHLY_STATUSES = ["draft", "shared"] as const;
export type MonthlyStatus = (typeof MONTHLY_STATUSES)[number];

/** Project side, GET /api/p/:projectId/monthly-reports */
export type MonthlyReportList = {
  rows: { month: string; label: string; status: MonthlyStatus | "not_started"; sharedAt: string | null; readAt: string | null; comments: number }[];
  /** The contract managers a shared report goes to ("A. Moreau"); empty = nobody to share with yet. */
  sharesWith: string[];
};

/** Project side, GET /api/p/:projectId/monthly-reports/:month */
export type MonthlyReportDetail = {
  month: string;
  /** "August 2026" */
  label: string;
  status: MonthlyStatus | "not_started";
  /** Live from the records while not shared; frozen once shared. */
  figures: MonthlyFigure[];
  /** "Done: A2.2 Regional workshop, Skopje, A1.3 ..." or null */
  done: string | null;
  summary: string;
  issues: string;
  nextMonth: string;
  sharedAt: string | null;
  /** "Shared 5 Sep by M. Kovač" */
  sharedLine: string | null;
  /** "Read by A. Moreau on 6 Sep" */
  readLine: string | null;
  comments: { by: string; at: string; text: string }[];
  canEdit: boolean;
  sharesWith: string[];
};

export const monthlyReportBody = z.object({
  /** Keys of the figures to share (the rest are left out). */
  include: z.array(z.string().max(40)).max(20),
  summary: textOrEmpty(6000),
  issues: textOrEmpty(6000),
  nextMonth: textOrEmpty(6000),
});

/** Contract manager side, GET /api/cm/:projectId/monthly */
export type CmMonthlyList = { header: CmHeader; rows: { month: string; label: string; isNew: boolean }[] };

/** Contract manager side, GET /api/cm/:projectId/monthly/:month */
export type CmMonthlyReport = {
  header: CmHeader;
  month: string;
  label: string;
  /** "Shared 5 Sep by M. Kovač, contract lead" */
  sharedLine: string;
  figures: { label: string; value: string }[];
  done: string | null;
  summary: string;
  issues: string;
  nextMonth: string;
  read: boolean;
  comments: { by: string; at: string; text: string; mine: boolean }[];
  /** The value to file a document with this report ("monthly:<id>"). */
  target: string;
};

export const cmCommentBody = z.object({ text: text(4000, "Comment") });

/** Project side, GET /api/p/:projectId/from-cm: everything the contract manager sent. */
export type FromCmInbox = {
  /** "A. Moreau, Contract manager, DG NEAR" */
  from: string[];
  rows: {
    id: string;
    kind: "decision" | "document" | "comment";
    what: string;
    filedUnder: string;
    /** In-app path of the record it is filed with, or null. */
    path: string | null;
    date: string;
    evidence: "filed" | "not_needed";
    fileId: string | null;
    isNew: boolean;
  }[];
};
